import { graphqlPost } from "api/graphqlClient";
import { RestError } from "api/restClient.types";
import { Button } from "components/Button/Button";
import {
    composeValidators,
    MAX_LENGTH,
    maxLengthValidator,
    oneLineValidator,
    requiredValidator,
} from "components/Form/Form.utils";
import { Form } from "components/Form/components/Form/Form";
import { ImageUpload } from "components/Form/components/ImageUpload/ImageUpload";
import { TextareaFormComponent } from "components/Form/components/Textarea/TextareaFormComponent";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import {
    ATTRIBUTES_PER_BATCH,
    NestedFormsDataProps,
    noDragClassName,
} from "components/ObjectTemplateCanvas/ObjectTemplateCanvas";
import { UnsavedChangesModal } from "components/Prompt/UnsavedChangesModal";
import Tooltip from "components/Tooltip/Tooltip";
import { FormField } from "config/config";
import { getIsDisabled, getIsReadonly } from "config/resourceUtils";
import objectTemplateConfig from "config/resources/object_template";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { ResourceEditFields } from "containers/Resources/components/ResourceEditFields/ResourceEditFields";
import { HiddenActionsComponent } from "containers/Resources/screens/ResourcesSingle/components/ResourcesSinglePageHeader/HiddenActionsComponent";
import { useCheckExistingResourceName } from "containers/Resources/screens/ResourcesSingle/useCheckExistingResourceName";
import { useDefaultLocation } from "containers/Resources/screens/ResourcesSingle/useDefaultLocation";
import {
    getDeletedBadges,
    newRecordId,
    saveNestedFormsForCanvas,
    storeFormDataForCanvas,
    submitSingleRecordForm,
} from "containers/Resources/screens/ResourcesSingle/utils";
import {
    AttributeImpactChange,
    classifyAttributeUpdate,
} from "config/resources/objectTemplateAttributeImpact";
import { confirmAttributeImpact } from "config/resources/objectTemplateAttributeImpactDialog";
import { updatedDiff } from "deep-object-diff";
import { Object_Template } from "graphql/generated/graphql";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Field, Form as FinalForm, FormSpy } from "react-final-form";
import { useDispatch } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { canvasPathFragment } from "utils/RouterManager/constants";
import { getRelativeUrl } from "utils/service";
import { DragAndDropCard } from "containers/DragAndDrop/DragAndDropCard";
import { useDragAndDropList } from "containers/DragAndDrop/useDragAndDropList";
import { AttributeCard } from "./AttributeCard";
import { formatObjectCount } from "./relationGraphFetch";
import { getAttributeSortOrderUpdates } from "./utils";
import styles from "./ObjectTemplateCard.module.scss";
import { getCanvasReadonlyAffordance } from "./components/components";

/**
 * Selector function to get object template data from the Redux store
 * @param state - The Redux state
 * @param key - The key to access the data store
 * @returns Array of object template data
 */
export const objectTemplateSelector = (state: any, key: string) => state?.dataStore?.[key] ?? [];

/**
 * Utility function to delete a nested form from the forms data
 * @param prevData - Previous nested forms data
 * @param id - The ID of the form to delete (defaults to newRecordId)
 * @returns Updated nested forms data without the deleted form
 */
export const deleteNestedForm = (prevData: { [key: string]: any }, id = newRecordId) => {
    const prevDataCopy = { ...prevData };
    delete prevDataCopy[id];
    return prevDataCopy;
};

export interface ObjectTemplateCardProps {
    data?: Object_Template;
    fetchObjectTemplateList: () => Promise<any>;
    deleteNewForm: () => void;
    setNestedFormsData: (data: any) => void;
    formStructure?: FormField[];
    nestedFormsData: NestedFormsDataProps;
    setLoading: (isLoading: boolean) => void;
    readOnly?: boolean;
    isFocused?: boolean;
    // Interactive template-canvas overview extras carried through the shared
    // additionalData bag (see utils getPositionedGraphList). The editable card
    // itself ignores them; the read-only card / getNode use them to expand exactly
    // one template into this editable form (item C1).
    expandedTemplateId?: string | null;
    onExpandTemplate?: (id: string) => void;
    onCollapseTemplate?: () => void;
}

/**
 * ObjectTemplateCard component that renders a form for creating or editing object templates
 * with support for nested forms, attribute management, and validation.
 *
 * This component handles:
 * - Form rendering and validation for object templates
 * - Nested forms data management for canvas integration
 * - Attribute creation and deletion
 * - Resource name uniqueness validation
 * - Form state persistence and dirty tracking
 * - Navigation and action handling
 *
 * @param data - The object template data to display and edit
 * @param fetchObjectTemplateList - Function to fetch updated object template list
 * @param deleteNewForm - Function to delete the new form
 * @param setNestedFormsData - Function to set nested forms data
 * @param formStructure - Structure of the form fields
 * @param nestedFormsData - Current nested forms data
 * @param setLoading - Function to set loading state
 * @returns JSX.Element - The rendered object template card form
 */
export const ObjectTemplateCard = ({
    data,
    fetchObjectTemplateList,
    deleteNewForm,
    setNestedFormsData,
    formStructure,
    nestedFormsData,
    setLoading,
    isFocused,
}: ObjectTemplateCardProps) => {
    const history = useHistory();
    const match = useRouteMatch<{ locationName: string; locationId: string }>(
        `/:locationName/:locationId/${canvasPathFragment}`
    );
    const currentLocation = match?.params?.locationName;
    const currentLocationId = match?.params?.locationId;

    // Prepare location data for the form
    const locationData = {
        [currentLocation === "project" ? "project_id" : "bundle_id"]: currentLocationId,
    };

    const dispatch = useDispatch();
    const objectTemplateResourceName = objectTemplateConfig.resourceName;

    const isDisabled = getIsDisabled(objectTemplateConfig, data);
    const isReadOnly = getIsReadonly(objectTemplateConfig, data);

    const initialAttributeList = useMemo(() => {
        return (
            data?.object_template_attribute_list_object_template_id
                ?.object_template_attribute_list ?? []
        );
    }, [data]);

    const totalAttributeCount = useMemo(() => {
        return (
            data?.object_template_attribute_list_object_template_id
                ?.object_template_attribute_count ?? 0
        );
    }, [data]);

    // Total number of (non-deleted) custom objects of this template. The count
    // is fetched with the canvas (customObjectLimit: 0 returns the count without
    // the objects), so it paints with the node — no extra pass needed. Uses the
    // same field as the "Number of custom objects" listing column.
    const objectCount = useMemo(
        (): number | undefined =>
            data?.custom_object_list_object_template_id?.custom_object_count ?? undefined,
        [data]
    );

    const [objectTemplateAttributeList, setObjectTemplateAttributeList] =
        useState(initialAttributeList);

    useEffect(() => {
        setObjectTemplateAttributeList(initialAttributeList);
        setOffset(0);
    }, [initialAttributeList]);

    // Drag-and-drop reorder state for the attribute list. `attributeCards` mirrors
    // objectTemplateAttributeList (re-synced whenever it changes, e.g. on pagination
    // or refetch) and holds the ephemeral order while dragging; persisted on drop.
    const { cards: attributeCards, onMoveCard: onMoveAttributeCard } =
        useDragAndDropList(objectTemplateAttributeList);

    const [offset, setOffset] = useState(0);
    const hasMore = useMemo(
        () => totalAttributeCount > objectTemplateAttributeList.length,
        [totalAttributeCount, objectTemplateAttributeList.length]
    );

    const fetchMoreAttributes = useCallback(
        async (offset: number) => {
            if (data?.id === undefined) {
                return;
            }

            try {
                const params = {
                    query: `query object_template($id: String!, $attributeLimit: Int, $attributeOffset: Int, $attributeOrder: String) {
                                object_template(id: $id) {
                                        id
                                        object_template_attribute_list_object_template_id(limit: $attributeLimit, offset: $attributeOffset, order: $attributeOrder) {
                                            object_template_attribute_count
                                            object_template_attribute_list {
                                            id
                                            name
                                            object_template_id
                                            datatype
                                            reference_object_template_id
                                            reference_record_type
                                            is_nullable
                                            is_unique
                                            is_hidden
                                            silent_update
                                            sort_order
                                        }
                                    }
                                }
                            }`,
                    variables: {
                        id: data?.id,
                        attributeLimit: ATTRIBUTES_PER_BATCH,
                        attributeOffset: offset,
                        attributeOrder: "+sort_order,+name",
                    },
                    allow_normal: true,
                    allow_deleted: true,
                };

                const result = (await graphqlPost(params)) as any;

                const attributes =
                    result?.data?.object_template?.object_template_attribute_list_object_template_id
                        ?.object_template_attribute_list ?? [];

                setObjectTemplateAttributeList((prev) => {
                    const newAttributes = attributes.filter(
                        (newAttr) => !prev.some((prevAttr) => prevAttr?.id === newAttr.id)
                    );
                    return [...prev, ...newAttributes];
                });
            } catch (error) {
                NotificationService.error(
                    <RestErrorNotification
                        error={error as RestError}
                        titleOverride="Cannot fetch attributes"
                    />
                );
                return {};
            } finally {
                setLoading(false);
            }
        },
        [data?.id, setLoading]
    );

    const observerInstance = useRef<IntersectionObserver | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (offset > 0) {
            fetchMoreAttributes(offset);
        }
    }, [fetchMoreAttributes, offset]);

    const lastItemRef = useCallback(
        (node: HTMLDivElement) => {
            if (observerInstance.current) {
                observerInstance.current.disconnect();
            }

            if (node) {
                observerInstance.current = new IntersectionObserver(
                    (entries) => {
                        if (entries[0].isIntersecting && hasMore) {
                            setOffset((prevOffset) => prevOffset + ATTRIBUTES_PER_BATCH);
                        }
                    },
                    {
                        root: scrollContainerRef.current,
                        rootMargin: "20px",
                        threshold: 0.1,
                    }
                );

                observerInstance.current.observe(node);
            }
        },
        [hasMore]
    );

    // State for showing/hiding the new attribute form
    const [isNewAttributeFormShown, setNewAttributeFormShown] = useState(false);

    /**
     * Button component for adding new attributes
     * @param isDisabled - Whether the button should be disabled
     * @returns JSX.Element - The add attribute button
     */
    const addAttributeButton = ({ isDisabled }) => (
        <Button
            disabled={isDisabled}
            variation="dark"
            onClick={() => setNewAttributeFormShown(true)}
            size="small"
            label="+ Add attribute"
        />
    );

    const hasChangesInCanvas = useMemo(
        () => Object.keys(nestedFormsData).length > 0,
        [nestedFormsData]
    );

    // Determine which function to use for saving and fetching data
    const saveAndFetchObjectTemplateList = useMemo(() => {
        return hasChangesInCanvas
            ? () => saveNestedFormsForCanvas({ nestedFormsData })
            : fetchObjectTemplateList;
    }, [fetchObjectTemplateList, hasChangesInCanvas, nestedFormsData]);

    /**
     * Delete an attribute and update the form
     * @param id - The ID of the attribute to delete
     * @returns Promise that resolves when the attribute is deleted
     */
    const deleteAttribute = useCallback(
        async (id) => {
            // Tell the user how many custom objects lose this attribute's data.
            const attributeName = initialAttributeList.find(
                (attribute: any) => attribute?.id === id
            )?.name;
            try {
                await confirmAttributeImpact({
                    objectTemplateId: data?.id,
                    changes: [{ kind: "delete", attributeName }],
                });
            } catch {
                return;
            }
            setLoading(true);
            return new Promise((resolve, reject) => {
                dispatch({
                    type: resourcesActions.START_DELETE_RESOURCE,
                    promise: { resolve, reject },
                    resourceName: "object_template_attribute",
                    id,
                });
            })
                .then((data: any) => {
                    // Remove the deleted attribute from nested forms data
                    setNestedAttributeFormsData((prevData) => {
                        const dataCopy = { ...prevData };
                        delete dataCopy[data?.id ?? newRecordId];
                        return dataCopy;
                    });
                })
                .then(() => {
                    saveAndFetchObjectTemplateList();
                })
                .catch(() => setLoading(false));
        },
        [data?.id, dispatch, initialAttributeList, saveAndFetchObjectTemplateList, setLoading]
    );

    /**
     * Persist a single attribute's sort_order via the standard update path.
     * Only sort_order is sent; the backend skips null body fields on update, so
     * other attribute fields are preserved (same pattern as resource_wrapper order).
     */
    const updateAttributeSortOrder = useCallback(
        ({ id, sort_order }: { id: string; sort_order: number }) =>
            new Promise((resolve, reject) => {
                dispatch({
                    type: resourcesActions.START_UPDATE_RESOURCE,
                    promise: { resolve, reject },
                    resourceName: "object_template_attribute",
                    variables: { id, sort_order },
                });
            }),
        [dispatch]
    );

    /**
     * Called when a drag ends: renumber the loaded attributes to their new index
     * and PATCH sort_order for every attribute whose position changed, then refetch
     * so the persisted order is authoritative. (Reordering is limited to the
     * currently-loaded page of attributes; see ATTRIBUTES_PER_BATCH pagination.)
     */
    const persistAttributeOrder = useCallback(() => {
        const updates = getAttributeSortOrderUpdates(attributeCards);
        if (!updates.length) {
            return Promise.resolve();
        }
        return Promise.all(updates.map(updateAttributeSortOrder))
            .then(() => fetchObjectTemplateList())
            .catch((error) => error);
    }, [attributeCards, updateAttributeSortOrder, fetchObjectTemplateList]);

    // Get default location information for the form
    const { location, locationId } = useDefaultLocation({
        data: locationData,
        resourceName: objectTemplateResourceName,
    });

    /**
     * Submit the form data to create or update the object template
     * @param formData - The form data to submit
     * @param form - The form instance
     * @returns Promise that resolves when the form is submitted
     */
    const submitForm = useCallback(
        (formData, form) => {
            const initialValues = form.getState().initialValues;
            let changedValues = updatedDiff(initialValues, formData);

            // Find formData keys that are not present in initialValues and add them to changedValues
            Object.keys(formData).forEach((key) => {
                if (initialValues[key] === undefined) {
                    changedValues[key] = formData[key];
                }
            });

            // Add location information for new records
            if (!formData?.id && location) {
                changedValues[location] = locationId;
            }
            const values = { ...changedValues, id: formData.id };

            return submitSingleRecordForm({
                formData: values,
                resourceConfig: objectTemplateConfig,
                dispatch,
                resourceName: objectTemplateResourceName,
                actionType: formData?.id
                    ? resourcesActions.START_UPDATE_RESOURCE
                    : resourcesActions.START_CREATE_RESOURCE,
            })
                ?.then(() => deleteNewForm())
                .then(() => fetchObjectTemplateList())
                .catch(() => setLoading(false));
        },
        [
            deleteNewForm,
            dispatch,
            fetchObjectTemplateList,
            location,
            locationId,
            objectTemplateResourceName,
            setLoading,
        ]
    );

    const formRef = useRef<any>(null);

    const { findExistingResourceName, existingResourceName, isCheckRunning } =
        useCheckExistingResourceName({
            formRef,
        });

    // State for managing the unsaved changes modal
    const [isModalVisible, setModalVisible] = useState(false);
    const applyAction = useRef<() => void>();

    // Clean up nested forms data when component unmounts
    useEffect(() => {
        return () => {
            setNestedFormsData((prevData) => deleteNestedForm(prevData, data?.id));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // State for managing nested attribute forms data
    const [nestedAttributeFormsData, setNestedAttributeFormsData] = useState({});

    // Show modal when there are unsaved changes in attribute forms
    useEffect(() => {
        setModalVisible(Object.keys(nestedAttributeFormsData).length > 0);
    }, [nestedAttributeFormsData]);

    /**
     * Save the form data, including nested attribute forms
     * @param formData - The form data to save
     * @param form - The form instance
     * @returns Promise that resolves when the form is saved
     */
    const saveForm = useCallback(
        (formData, form) => {
            setLoading(true);
            const isPristine = cloneDeep(form).getState().pristine;

            // One impact notice for all attribute cards saved together.
            const impactChanges = Object.entries(nestedAttributeFormsData)
                .map(([key, attributeForm]: [string, any]) => {
                    const state = attributeForm?.getState?.();
                    if (!state) {
                        return null;
                    }
                    return key === newRecordId
                        ? { kind: "add" as const, attributeName: state.values?.name }
                        : classifyAttributeUpdate(state.initialValues, state.values);
                })
                .filter((change): change is AttributeImpactChange => Boolean(change));

            return confirmAttributeImpact({ objectTemplateId: data?.id, changes: impactChanges })
                .then(() => saveNestedFormsForCanvas({ nestedFormsData: nestedAttributeFormsData }))
                .then(() => {
                    if (!isPristine) {
                        submitForm(formData, form);
                    } else {
                        fetchObjectTemplateList();
                    }
                })
                .catch(() => {
                    setLoading(false);
                });
        },
        [data?.id, fetchObjectTemplateList, nestedAttributeFormsData, setLoading, submitForm]
    );

    /**
     * Reset the form to its initial state
     * @returns Promise that resolves when the form is reset
     */
    const resetForm = useCallback(() => {
        formRef.current.restart();
        return Promise.resolve({});
    }, []);

    const attributeCardProps = useMemo(
        () => ({
            deleteNewForm: () => setNewAttributeFormShown(false),
            nestedAttributeFormsData,
            setNestedAttributeFormsData,
        }),
        [nestedAttributeFormsData]
    );

    // Track whether nested attribute forms have changes
    const [isPristineNestedData, setPristineNestedData] = useState(true);

    useEffect(() => {
        if (Object.values(nestedAttributeFormsData).length > 0) {
            setPristineNestedData(false);
        } else {
            setPristineNestedData(true);
        }
    }, [nestedAttributeFormsData]);

    // Check if the main form is pristine (has no changes)
    const isFormPristine = formRef.current ? formRef.current.getState().pristine === true : true;

    // Store form data for canvas integration when form state changes
    useEffect(() => {
        if (isFormPristine) {
            storeFormDataForCanvas({
                pristine: isPristineNestedData,
                setNestedFormSaveData: setNestedFormsData,
                recordId: data?.id,
                form: formRef.current,
                isNew: data?.id === undefined,
                newRecordId,
            });
        }
    }, [data?.id, isFormPristine, isPristineNestedData, setNestedFormsData]);

    // Determine if this form can be edited (no other forms have changes)
    const canEdit = useMemo(
        () =>
            Object.keys(nestedFormsData).length === 0 ||
            Object.keys(nestedFormsData).some((key) => key === (data?.id ?? newRecordId)),
        [data?.id, nestedFormsData]
    );

    // Modify the object template config to add after-action handlers
    const changedObjectTemplateConfig = useMemo(() => {
        const objectTemplateConfigCopy = cloneDeep(objectTemplateConfig);
        const hiddenActions = objectTemplateConfigCopy.actions.hidden.map((action) => {
            if (action.key === "move_to_location" || action.key === "deletePermanently") {
                action.hasAfterAction = true;
            }
            return action;
        });
        objectTemplateConfigCopy.actions.hidden = hiddenActions;
        return objectTemplateConfigCopy;
    }, []);

    return (
        <div
            className={`${styles.formWrapper} ${canEdit ? "" : styles.formWrapperDisabled} ${
                isFocused ? styles.focused : ""
            }`}
        >
            <FinalForm
                onSubmit={saveForm}
                initialValues={data ?? {}}
                destroyOnUnregister
                keepDirtyOnReinitialize
            >
                {({ handleSubmit, pristine, values, form, hasValidationErrors, initialValues }) => {
                    formRef.current = form;

                    return (
                        <Form onSubmit={handleSubmit}>
                            <div className={styles.actionButtonContainer}>
                                <div className={styles.badgesContainer}>
                                    {getCanvasReadonlyAffordance({
                                        resourceData: data,
                                        resourceConfig: objectTemplateConfig,
                                    })}
                                    {getDeletedBadges({ resourceData: data, size: "medium" })}
                                </div>
                                <div>
                                    <Button
                                        submit
                                        disabled={
                                            (pristine && isPristineNestedData) ||
                                            hasValidationErrors ||
                                            existingResourceName === values?.name ||
                                            isCheckRunning
                                        }
                                        icon={IconCollection.saveOutline}
                                        variation="icon"
                                    />
                                    {/* Open in new tab button */}
                                    <Button
                                        disabled={!data?.id}
                                        icon={IconCollection.openInNew}
                                        variation="icon"
                                        onClick={() =>
                                            history.push(
                                                data?.id
                                                    ? getRelativeUrl(
                                                          objectTemplateResourceName,
                                                          data.id
                                                      )
                                                    : ""
                                            )
                                        }
                                    />
                                    {/* Hidden actions component for additional actions */}
                                    <HiddenActionsComponent
                                        pristine={pristine}
                                        resourceData={data}
                                        resourceName={objectTemplateResourceName}
                                        resourceConfig={changedObjectTemplateConfig}
                                        afterAction={fetchObjectTemplateList}
                                        fetchResourceData={fetchObjectTemplateList}
                                        setModalVisible={setModalVisible}
                                        applyAction={applyAction}
                                        hiddenActionList={["duplicate"]}
                                    />
                                    {/* Delete button for new forms */}
                                    {data?.id === undefined && (
                                        <Button
                                            icon={IconCollection.deleteOutline}
                                            variation="icon"
                                            onClick={() => deleteNewForm()}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className={styles.headerWrapper}>
                                <div className={styles.iconField}>
                                    <Field
                                        name="icon"
                                        component={ImageUpload}
                                        fieldProps={{ renderIconOnly: true }}
                                        fieldAttr={{ disabled: isDisabled }}
                                    />
                                </div>
                                <div className={styles.nameFieldContainer}>
                                    <Field
                                        name="name"
                                        validate={composeValidators(
                                            requiredValidator,
                                            maxLengthValidator(MAX_LENGTH),
                                            oneLineValidator
                                        )}
                                    >
                                        {({ input, meta }) => {
                                            return (
                                                <TextareaFormComponent
                                                    input={input}
                                                    meta={meta}
                                                    fieldProps={{
                                                        onBlur:
                                                            values?.name ===
                                                            (initialValues as any).name
                                                                ? input.onBlur
                                                                : (e) =>
                                                                      findExistingResourceName(
                                                                          values?.name,
                                                                          e
                                                                      )
                                                                          ?.then((data: any) => {
                                                                              if (
                                                                                  data &&
                                                                                  !pristine
                                                                              ) {
                                                                                  const {
                                                                                      resource: {
                                                                                          name,
                                                                                          resource_type,
                                                                                      },
                                                                                  } = data;

                                                                                  const errorMessage =
                                                                                      name &&
                                                                                      name ===
                                                                                          values?.name
                                                                                          ? `Cannot save object template. Another ${resource_type?.toLowerCase()} with the name "${
                                                                                                values?.name
                                                                                            }" already exists.`
                                                                                          : "";
                                                                                  if (
                                                                                      errorMessage.length
                                                                                  ) {
                                                                                      NotificationService.error(
                                                                                          errorMessage
                                                                                      );
                                                                                  }
                                                                              }
                                                                          })
                                                                          .catch((error) => error)
                                                                          .finally(() =>
                                                                              input.onBlur()
                                                                          ),
                                                    }}
                                                    fieldAttr={{
                                                        required: true,
                                                        placeholder: "Name...",
                                                        disabled: isDisabled,
                                                    }}
                                                />
                                            );
                                        }}
                                    </Field>
                                </div>
                            </div>

                            {/* Live count of custom objects of this template (lazy: arrives
                                with the canvas fetch). Hidden for the unsaved new-template form. */}
                            {data?.id && (
                                <div className={styles.objectCount}>
                                    {formatObjectCount(objectCount)}
                                </div>
                            )}

                            {/* Form fields wrapper with no-drag class for ReactFlow */}
                            <div className={`${styles.formFieldsWrapper} ${noDragClassName}`}>
                                <ResourceEditFields
                                    formStructure={formStructure}
                                    isReadOnly={isReadOnly}
                                    isDisabled={isDisabled}
                                    resourceTypeName={objectTemplateResourceName}
                                    recordId={data?.id || ""}
                                    writeOnlyFieldsWithData={{}}
                                    currentValues={data}
                                    additionalFieldProps={{ renderIconOnly: true }}
                                />
                            </div>

                            {/* Form spy to track form state changes */}
                            <FormSpy
                                onChange={({ pristine }) => {
                                    if (isPristineNestedData) {
                                        storeFormDataForCanvas({
                                            pristine: pristine,
                                            setNestedFormSaveData: setNestedFormsData,
                                            recordId: data?.id,
                                            form,
                                            isNew: data?.id === undefined,
                                            newRecordId,
                                        });
                                    }
                                }}
                            />

                            {/* Unsaved changes modal */}
                            {applyAction.current && (
                                <UnsavedChangesModal
                                    applyAction={applyAction.current}
                                    saveContent={
                                        hasValidationErrors
                                            ? undefined
                                            : (handleSubmit as () => Promise<any>)
                                    }
                                    setModalVisible={setModalVisible}
                                    isModalVisible={isModalVisible}
                                    resetForm={resetForm}
                                />
                            )}
                        </Form>
                    );
                }}
            </FinalForm>

            {/* Attributes section */}
            <div className={`${styles.attributeListContainer} ${noDragClassName}`}>
                <div className={styles.attributesTitle}>Attributes</div>
                <div className={styles.attributesFormListContainer} ref={scrollContainerRef}>
                    {attributeCards?.length
                        ? attributeCards.map((attribute, index, array) => (
                              <div
                                  key={attribute?.id}
                                  ref={index === array.length - 1 ? lastItemRef : null}
                              >
                                  <DragAndDropCard
                                      id={attribute?.id ?? ""}
                                      index={index}
                                      onMoveCard={onMoveAttributeCard}
                                      applyAction={persistAttributeOrder}
                                      type="object_template_attribute"
                                      canDrag={
                                          array.length > 1 &&
                                          !isDisabled &&
                                          !isReadOnly &&
                                          isPristineNestedData
                                      }
                                  >
                                      <AttributeCard
                                          data={attribute}
                                          deleteAttribute={deleteAttribute}
                                          isDisabled={isDisabled}
                                          isReadOnly={isReadOnly}
                                          {...attributeCardProps}
                                      />
                                  </DragAndDropCard>
                              </div>
                          ))
                        : null}
                </div>

                {/* Render new attribute form when shown */}
                {isNewAttributeFormShown && data?.id && (
                    <AttributeCard
                        data={null}
                        objectTemplateId={data.id}
                        key={newRecordId}
                        isDisabled={false}
                        isReadOnly={false}
                        {...attributeCardProps}
                    />
                )}

                {/* Add attribute button with conditional tooltips */}
                <div className={styles.attributeAddButton}>
                    {data?.id && !isNewAttributeFormShown ? (
                        addAttributeButton({ isDisabled })
                    ) : isNewAttributeFormShown ? (
                        <Tooltip overlay="Save new attribute first">
                            {addAttributeButton({ isDisabled: true })}
                        </Tooltip>
                    ) : (
                        <Tooltip overlay="Save before adding attributes">
                            {addAttributeButton({ isDisabled: true })}
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};
